import { Alert, Button, Divider, Tabs } from 'antd';
import { Suspense, useCallback, useContext, useState } from 'react';
import { formatCurrency } from '~/utils/currency';
import { PaymentMethod, PAYMENT_METHOD_TEXT, Payment } from '~/types/Payment';
import CashTab from './components/CashTab';
import OtherPaymentTab from './components/OtherPaymentTab';
import { DrawerContext } from '~/providers/DrawerProvider';
import './style.css';
import SplitPaymentTab from './components/SplitPaymentTab';
import EDCTab from './components/EDCTab';
import WalletTab from './components/WalletTab';
import TransferTab from './components/TransferTab';
import ErrorBoundary from '~/App/ErrorBoundary';
import SpinnerOverlay from '~/App/components/SpinnerOverlay';
import { usePriceRounding } from './hooks/usePriceRounding';
import { State } from '~/types';

type SubmitCallback = (params: {
  roundingAmount: number;
  payments: Payment[];
}) => void;

enum TabKey {
  Cash = 'cash',
  EDC = 'edc',
  Wallet = 'wallet',
  Transfer = 'transfer',
  Other = 'other',
  Split = 'split',
}

const PaymentPanel = ({
  totalPrice: rawTotalPrice,
  disableRounding,
  onSubmit,
}: {
  totalPrice: number;
  disableRounding?: boolean;
  onSubmit: SubmitCallback;
}) => {
  const [payment, setPayment] = useState<Payment[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const priceRoundingState = usePriceRounding(rawTotalPrice);
  const [isRounding, setIsRounding] = useState(!disableRounding);
  const [tabKey, setTabKey] = useState<TabKey>(TabKey.Cash);

  const isCashPaymentAndRounding =
    tabKey === TabKey.Cash &&
    isRounding &&
    priceRoundingState.type === State.Success;

  const totalPrice = isCashPaymentAndRounding
    ? rawTotalPrice - priceRoundingState.data
    : rawTotalPrice;

  const onCashTabSubmit = (amount: number) => {
    setError(null);
    setPayment([{ method: PaymentMethod.Cash, amount, description: '' }]);
  };

  const onOtherPaymentTabSubmit = ({
    method,
    description,
  }: {
    method: PaymentMethod;
    description: string;
  }) => {
    setError(null);
    setPayment([{ method, amount: totalPrice, description }]);
  };

  const onSplitPaymentTabSubmit = (items: Payment[]) => {
    setError(null);
    setPayment(items);
  };

  const tabItems = [
    {
      label: 'Tunai',
      key: 'cash',
      children: (
        <CashTab
          isRounding={isRounding}
          setIsRounding={setIsRounding}
          totalPrice={totalPrice}
          onSubmit={onCashTabSubmit}
          disableRounding={disableRounding}
        />
      ),
    },
    {
      label: 'EDC',
      key: 'edc',
      children: <EDCTab onSubmit={onOtherPaymentTabSubmit} />,
    },
    {
      label: 'e-Wallet',
      key: 'wallet',
      children: <WalletTab onSubmit={onOtherPaymentTabSubmit} />,
    },
    {
      label: 'Transfer',
      key: 'transfer',
      children: <TransferTab onSubmit={onOtherPaymentTabSubmit} />,
    },
    {
      label: 'Lainnya',
      key: 'other',
      children: <OtherPaymentTab onSubmit={onOtherPaymentTabSubmit} />,
    },
    {
      label: 'Split Payment',
      key: 'split',
      children: (
        <SplitPaymentTab
          totalPrice={totalPrice}
          onSubmit={onSplitPaymentTabSubmit}
        />
      ),
    },
  ];

  const totalPayment = payment
    ? payment.reduce((acc, cur) => {
        acc += cur.amount;
        return acc;
      }, 0)
    : 0;

  const nonCashPayment = payment
    ? payment.reduce((acc, { method, amount }) => {
        if (method !== PaymentMethod.Cash) {
          acc += amount;
        }
        return acc;
      }, 0)
    : 0;

  const handleSubmit = () => {
    setError(null);
    if (!payment) {
      setError('Pilih metode dan jumlah pembayaran');
      return;
    }
    if (totalPayment < totalPrice) {
      setError('Pembayaran kurang dari tagihan!');
      return;
    }
    if (nonCashPayment > totalPrice) {
      setError('Pembayaran non tunai tidak bisa melebihi tagihan');
      return;
    }
    onSubmit({
      roundingAmount: isCashPaymentAndRounding ? priceRoundingState.data : 0,
      payments: payment,
    });
  };

  return (
    <div>
      {error ? <Alert message={error} type="error" showIcon /> : null}
      {isCashPaymentAndRounding ? (
        <>
          <div className="payment-row">
            <span className="font-medium text-base">Subtotal</span>
            <span className="font-medium text-base">
              {formatCurrency(rawTotalPrice)}
            </span>
          </div>
          <Divider style={{ margin: 0 }} />
          <div className="payment-row">
            <span className="font-medium text-base">Rounding</span>
            <span className="font-medium text-base">{`- ${formatCurrency(
              priceRoundingState.data
            )}`}</span>
          </div>
          <Divider style={{ margin: 0 }} />
        </>
      ) : null}
      <div className="payment-row">
        <span className="font-medium text-2xl">Tagihan</span>
        <span className="font-medium text-2xl">
          {formatCurrency(totalPrice)}
        </span>
      </div>
      {payment ? (
        <>
          <Divider style={{ margin: 0 }} />
          {payment.length > 1 ? (
            <>
              {payment.map(({ method, amount, description }) => (
                <div
                  className="payment-row"
                  style={{ margin: '4px 0' }}
                  key={`${method}-${amount}`}
                >
                  <div>
                    <p className="split-payment-item">
                      {PAYMENT_METHOD_TEXT[method]}
                    </p>
                    {description ? (
                      <span className="payment-desc">{description}</span>
                    ) : null}
                  </div>
                  <p className="split-payment-item">{formatCurrency(amount)}</p>
                </div>
              ))}
              <div className="payment-row" style={{ marginTop: 8 }}>
                <span className="payment-bold">Total Bayar</span>
                <span className="price payment-bold">
                  {formatCurrency(totalPayment)}
                </span>
              </div>
            </>
          ) : (
            <div className="payment-row">
              <div>
                <p className="payment-bold">{`Pembayaran (${
                  PAYMENT_METHOD_TEXT[payment[0].method]
                })`}</p>
                {payment[0].description ? (
                  <span className="payment-desc">{payment[0].description}</span>
                ) : null}
              </div>
              <span className="payment-bold price">
                {formatCurrency(totalPayment)}
              </span>
            </div>
          )}
          <Divider style={{ margin: 0 }} />
          <div className="payment-row">
            <span className="payment-bold">Kembalian</span>
            <span className="payment-bold">
              {formatCurrency(totalPayment - totalPrice)}
            </span>
          </div>
        </>
      ) : null}
      <Tabs
        size="large"
        activeKey={tabKey}
        items={tabItems}
        style={{ marginBottom: 16 }}
        onTabClick={(value) => {
          setTabKey(value as TabKey);
          setPayment(null);
        }}
      />

      <Button
        onClick={handleSubmit}
        disabled={payment == null}
        type="primary"
        size="large"
        block
      >
        Bayar
      </Button>
    </div>
  );
};

const useOpenPaymentPanel = () => {
  const { openDrawer } = useContext(DrawerContext);
  return useCallback(
    ({
      totalPrice,
      disableRounding,
      onSubmit,
    }: {
      totalPrice: number;
      disableRounding?: boolean;
      onSubmit: SubmitCallback;
    }) => {
      openDrawer(
        ({ closeDrawer }) => {
          const handleSubmit: SubmitCallback = (params) => {
            closeDrawer();
            onSubmit(params);
          };
          return (
            <ErrorBoundary>
              <Suspense fallback={<SpinnerOverlay show />}>
                <PaymentPanel
                  totalPrice={totalPrice}
                  disableRounding={disableRounding}
                  onSubmit={handleSubmit}
                />
              </Suspense>
            </ErrorBoundary>
          );
        },
        { height: '95%', placement: 'bottom' }
      );
    },
    []
  );
};

export default useOpenPaymentPanel;
